import { supabase } from "./supabase.js";
import { getSession } from "./auth.js";
import { updateGroup } from "./groups.js";

// Share a collection as a read-only link (roadmap #58,
// migrations/2026-10-16-collections.sql): a snapshot of its name and items in
// `shared_collections`, read by anyone with the link through
// get_shared_collection(token). Sharing again refreshes the snapshot.

export function randomToken(n = 22) {
	const abc = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
	const bytes = new Uint8Array(n);
	crypto.getRandomValues(bytes);
	return [...bytes].map((b) => abc[b % abc.length]).join("");
}

export function shareUrl(token) {
	return `${location.origin}${location.pathname}#/shared/${token}`;
}

// What a stranger sees: no ids, notes, reviews or loans — title, year, cover,
// category, status and rating.
export function snapshotItems(members) {
	return members.slice(0, 500).map((m) => ({
		title: m.title ?? "",
		year: m.release_year ?? m.premiered_year ?? m.published_year ?? null,
		cover_url: m.cover_url ?? null,
		media_type: m.media_type ?? null,
		status: m.engagement_status ?? null,
		rating: m.rating ?? null,
	}));
}

const notSetUp = (error) => /shared_collections|get_shared_collection|share_token|schema cache|does not exist/i.test(error?.message ?? "");

// → the link. Throws { code: "NOT_SET_UP" } before the SQL step has run.
export async function shareCollection(group, members) {
	const session = await getSession();
	if (!session?.user?.id) {
		const err = new Error("Sign in to share collections.");
		err.code = "SIGNED_OUT";
		throw err;
	}
	const token = group.share_token ?? randomToken();
	const { error } = await supabase.from("shared_collections").upsert(
		{ token, user_id: session.user.id, group_id: group.id, name: group.name, items: snapshotItems(members), updated_at: new Date().toISOString() },
		{ onConflict: "token" },
	);
	if (error) {
		const err = new Error(notSetUp(error) ? "Sharing isn't set up on the server yet." : error.message);
		if (notSetUp(error)) err.code = "NOT_SET_UP";
		throw err;
	}
	if (group.share_token !== token) await updateGroup(group.id, { share_token: token });
	return shareUrl(token);
}

export async function stopSharing(group) {
	if (!group.share_token) return;
	const { error } = await supabase.from("shared_collections").delete().eq("token", group.share_token);
	if (error && !notSetUp(error)) throw error;
	await updateGroup(group.id, { share_token: null });
}

// Public: anyone with the link. → { name, items, updated_at } | null.
export async function getSharedCollection(token) {
	const { data, error } = await supabase.rpc("get_shared_collection", { p_token: token });
	if (error) throw error;
	return Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
}
